import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Calendar, 
  Laptop, 
  Car, 
  Shield, 
  Gift, 
  GraduationCap,
  Package
} from 'lucide-react'
import { useBudgetAssistantStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'

interface YearlyBudgetStepProps {
  onNext: () => void
  onSkip: () => void
}

interface ExpenseCategory {
  id: string
  icon: any
  title: string
  description: string
  placeholder: string
  color: string
}

export function YearlyBudgetStep({ onNext, onSkip }: YearlyBudgetStepProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { responses, updateResponses } = useBudgetAssistantStore()
  
  // Simple formatting functions with fallback for undefined currency
  const getCurrencySymbol = () => (responses?.currency === 'EUR' ? '€' : '$')
  const formatNumber = (amount: number, withSymbol = false) => {
    const formatted = amount.toLocaleString()
    return withSymbol ? `${getCurrencySymbol()}${formatted}` : formatted
  }
  const formatInputValue = (value: string) => value.replace(/[^0-9.]/g, '')
  
  const [expenses, setExpenses] = useState<Record<string, string>>({
    vacation: '',
    tech: '',
    vehicle: '',
    insurance: '',
    holidays: '',
    education: '',
    other: ''
  })

  const [convertToMonthly, setConvertToMonthly] = useState(true)

  // Create refs for each input field
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({
    vacation: null,
    tech: null,
    vehicle: null,
    insurance: null,
    holidays: null,
    education: null,
    other: null
  })

  // Load saved data on mount
  useEffect(() => {
    if (responses.yearlyExpenses) {
      setExpenses({
        vacation: responses.yearlyExpenses.vacation ? responses.yearlyExpenses.vacation.toString() : '',
        tech: responses.yearlyExpenses.tech ? responses.yearlyExpenses.tech.toString() : '',
        vehicle: responses.yearlyExpenses.vehicle ? responses.yearlyExpenses.vehicle.toString() : '',
        insurance: responses.yearlyExpenses.insurance ? responses.yearlyExpenses.insurance.toString() : '',
        holidays: responses.yearlyExpenses.holidays ? responses.yearlyExpenses.holidays.toString() : '',
        education: responses.yearlyExpenses.education ? responses.yearlyExpenses.education.toString() : '',
        other: responses.yearlyExpenses.other ? responses.yearlyExpenses.other.toString() : ''
      })
    }
    // Load the saved toggle preference
    if (responses.convertYearlyToMonthly !== undefined) {
      setConvertToMonthly(responses.convertYearlyToMonthly)
    }
  }, [responses])

  const categories: ExpenseCategory[] = [
    {
      id: 'vacation',
      icon: Calendar,
      title: t('budgetAssistant.yearly.vacation.question', 'How much do you spend on vacations or trips each year?'),
      description: t('budgetAssistant.yearly.vacation.questionDescription', 'Include flights, hotels, vacation activities, and travel expenses'),
      placeholder: '0',
      color: 'text-blue-500'
    },
    {
      id: 'tech',
      icon: Laptop,
      title: t('budgetAssistant.yearly.tech.question', 'Do you buy new electronics or gadgets regularly?'),
      description: t('budgetAssistant.yearly.tech.questionDescription', 'Phones, laptops, tablets, gaming equipment, and tech upgrades'),
      placeholder: '0',
      color: 'text-purple-500'
    },
    {
      id: 'vehicle',
      icon: Car,
      title: t('budgetAssistant.yearly.vehicle.question', 'Do you have yearly vehicle expenses?'),
      description: t('budgetAssistant.yearly.vehicle.questionDescription', 'Car maintenance, tires, registration fees, and major repairs'),
      placeholder: '0',
      color: 'text-amber-500'
    },
    {
      id: 'insurance',
      icon: Shield,
      title: t('budgetAssistant.yearly.insurance.question', 'Do you pay for insurance or annual fees?'),
      description: t('budgetAssistant.yearly.insurance.questionDescription', 'Home, health, life insurance, or annual membership fees'),
      placeholder: '0',
      color: 'text-emerald-500'
    },
    {
      id: 'holidays',
      icon: Gift,
      title: t('budgetAssistant.yearly.holidays.question', 'How much do you spend on holidays and gifts?'),
      description: t('budgetAssistant.yearly.holidays.questionDescription', 'Birthday gifts, holiday shopping, special occasions throughout the year'),
      placeholder: '0',
      color: 'text-red-500'
    },
    {
      id: 'education',
      icon: GraduationCap,
      title: t('budgetAssistant.yearly.education.question', 'Do you pay for education or courses?'),
      description: t('budgetAssistant.yearly.education.questionDescription', 'Tuition, school fees, online courses, certifications, or training'),
      placeholder: '0',
      color: 'text-green-500'
    },
    {
      id: 'other',
      icon: Package,
      title: t('budgetAssistant.yearly.other.question', 'Any other large yearly expenses?'),
      description: t('budgetAssistant.yearly.other.questionDescription', 'Home repairs, appliances, memberships, or other annual costs'),
      placeholder: '0',
      color: 'text-gray-500'
    }
  ]

  const handleExpenseChange = useCallback((categoryId: string, value: string) => {
    const numericValue = formatInputValue(value)
    setExpenses(prev => ({
      ...prev,
      [categoryId]: numericValue
    }))
  }, [formatInputValue])

  // Function to move to next input field
  const moveToNextField = useCallback((currentField: string) => {
    const fieldOrder = ['vacation', 'tech', 'vehicle', 'insurance', 'holidays', 'education', 'other']
    const currentIndex = fieldOrder.indexOf(currentField)
    
    if (currentIndex < fieldOrder.length - 1) {
      const nextField = fieldOrder[currentIndex + 1]
      const nextRef = inputRefs.current[nextField]
      
      if (nextRef) {
        nextRef.focus()
      }
    }
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent, categoryId: string) => {
    if (e.key === 'Enter' && categoryId !== 'other') {
      e.preventDefault()
      moveToNextField(categoryId)
    }
  }, [moveToNextField])

  const handleNext = useCallback(async () => {
    // Store yearly expenses data
    const yearlyExpensesData = {
      vacation: parseFloat(expenses.vacation) || 0,
      tech: parseFloat(expenses.tech) || 0,
      vehicle: parseFloat(expenses.vehicle) || 0,
      insurance: parseFloat(expenses.insurance) || 0,
      holidays: parseFloat(expenses.holidays) || 0,
      education: parseFloat(expenses.education) || 0,
      other: parseFloat(expenses.other) || 0
    }

    try {
      await updateResponses({
        yearlyExpenses: yearlyExpensesData,
        convertYearlyToMonthly: convertToMonthly
      }, 'yearly_expenses')
      onNext()
    } catch (error) {
      console.error('Error saving yearly expenses data:', error)
      // Still proceed even if save fails
      onNext()
    }
  }, [expenses, convertToMonthly, updateResponses, onNext])

  const totalYearlyExpenses = Object.values(expenses).reduce((sum, value) => {
    return sum + (parseFloat(value) || 0)
  }, 0)

  const totalMonthlyEquivalent = totalYearlyExpenses / 12

  const hasAnyExpenses = totalYearlyExpenses > 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center justify-center">
            <Calendar className="w-7 h-7 text-blue-500" />
          </div>
        </div>
        
        <div>
          <h2 className="text-2xl font-bold text-primary mb-2">
            {t('budgetAssistant.yearly.question', 'Do you have any major yearly expenses?')}
          </h2>
          <p className="text-secondary leading-relaxed">
            {t('budgetAssistant.yearly.questionSubtitle', 'We\'ll help you plan for these expenses so they don\'t surprise your monthly budget.')}
          </p>
        </div>
      </div>

      {/* Toggle Option */}
      <div className="bg-background-secondary/50 border border-border rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-primary mb-1">
              {convertToMonthly 
                ? t('budgetAssistant.yearly.toggle.monthly', 'Convert to Monthly Savings')
                : t('budgetAssistant.yearly.toggle.yearly', 'Keep as Yearly Budgets')
              }
            </h3>
            <p className="text-sm text-secondary">
              {convertToMonthly
                ? t('budgetAssistant.yearly.toggle.monthlyDescription', 'Divide yearly amounts by 12 and save monthly to avoid budget shocks')
                : t('budgetAssistant.yearly.toggle.yearlyDescription', 'Keep as separate yearly budgets that you manage annually')
              }
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={convertToMonthly}
              onChange={(e) => setConvertToMonthly(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Expense Categories */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className="bg-background-secondary/50 border border-border rounded-xl p-4 space-y-3"
          >
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-opacity-10 ${category.color.replace('text-', 'bg-').replace('-500', '-500/10')} border border-opacity-20 ${category.color.replace('text-', 'border-').replace('-500', '-500/20')}`}>
                <category.icon className={`w-5 h-5 ${category.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-primary text-sm mb-1">
                  {category.title}
                </h3>
                <p className="text-xs text-secondary leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-primary">{getCurrencySymbol()}</span>
              <input
                ref={(ref) => (inputRefs.current[category.id] = ref)}
                type="text"
                value={expenses[category.id]}
                onChange={(e) => handleExpenseChange(category.id, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, category.id)}
                placeholder={category.placeholder}
                className="flex-1 h-10 bg-background-secondary/50 border border-border rounded-lg px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Total Summary */}
      {hasAnyExpenses && (
        <div className="bg-background-secondary/50 border border-primary rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-primary">
              {t('budgetAssistant.yearly.total', 'Total Yearly Expenses')}
            </h3>
            <span className="text-xl font-bold text-primary">
              {formatNumber(totalYearlyExpenses, true)}
            </span>
          </div>
          
          {convertToMonthly && (
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-sm text-secondary">
                {t('budgetAssistant.yearly.monthlyEquivalent', 'Monthly savings needed:')}
              </span>
              <span className="font-semibold text-green-600">
                {formatNumber(totalMonthlyEquivalent, true)}
              </span>
            </div>
          )}
          
          <p className="text-sm text-secondary mt-2">
            {convertToMonthly 
              ? t('budgetAssistant.yearly.totalDescriptionMonthly', 'We\'ll create monthly savings goals for these expenses')
              : t('budgetAssistant.yearly.totalDescriptionYearly', 'These will be added as annual budget categories')
            }
          </p>
        </div>
      )}

      {/* Tips */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
        <h3 className="font-semibold text-green-700 dark:text-green-400 text-center mb-3">
          {t('budgetAssistant.yearly.tips.title', 'Smart Planning Tips')}
        </h3>
        
        <div className="space-y-2 text-sm text-green-800 dark:text-green-300">
          <p>• {t('budgetAssistant.yearly.tips.average', 'Use your average yearly amount for variable expenses')}</p>
          <p>• {t('budgetAssistant.yearly.tips.monthly', 'Monthly savings prevent "budget shock" when these expenses arise')}</p>
          <p>• {t('budgetAssistant.yearly.tips.skip', 'Skip categories that don\'t apply to your lifestyle')}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6">
        <button
          onClick={handleNext}
          className="flex-1 bg-primary text-white py-3 px-6 rounded-xl font-semibold hover:bg-primary/90 transition-colors"
        >
          {t('budgetAssistant.common.continue', 'Continue')}
        </button>
        
        <button
          onClick={onSkip}
          className="px-6 py-3 text-secondary hover:text-primary transition-colors"
        >
          {t('budgetAssistant.common.skip', 'Skip this step')}
        </button>
      </div>
    </div>
  )
} 